import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

const platforms = [
  { name: "Baselinker", to: "/platformy/baselinker" },
  { name: "SellAsist", to: "/platformy/sellasist" },
  { name: "Shoper", to: "/platformy/shoper" },
  { name: "ShopGold", to: "/platformy/shopgold" },
  { name: "Shopify", to: "/platformy/shopify" },
  { name: "PrestaShop", to: "/platformy/prestashop" },
] as const;

// Sekcje na stronie głównej, które chcemy śledzić scroll-spyem.
const SECTION_IDS = ["uslugi", "proces", "mapa", "faq", "kontakt"] as const;
type SectionId = (typeof SECTION_IDS)[number];

export function Header() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [platOpen, setPlatOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId | "home" | null>(
    isHome ? "home" : null,
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy — śledzi która sekcja jest aktualnie na ekranie (działa tylko na "/").
  useEffect(() => {
    if (!isHome) {
      setActiveSection(null);
      return;
    }

    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Wybieramy sekcję, która jest najbardziej "w środku" viewportu.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id as SectionId);
        } else {
          // Jeśli żadna sekcja nie jest widoczna, prawdopodobnie jesteśmy na samej górze (Hero).
          if (window.scrollY < 200) setActiveSection("home");
        }
      },
      {
        // Aktywny gdy sekcja zajmuje środkowy pas ekranu — eliminuje migotanie na granicach.
        rootMargin: "-40% 0px -50% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [isHome]);

  const navItems = [
    { key: "home" as const, label: "Strona główna", href: "/", isLink: true },
    { key: "uslugi" as const, label: "Usługi", href: "/#uslugi", isLink: false },
    { key: "cennik" as const, label: "Cennik", href: "/cennik", isLink: true },
    { key: "kalkulator" as const, label: "Kalkulator", href: "/kalkulator", isLink: true },
    { key: "blog" as const, label: "Blog", href: "/blog", isLink: true },
    { key: "porownania" as const, label: "Porównania", href: "/porownanie", isLink: true },
    { key: "faq" as const, label: "FAQ", href: "/#faq", isLink: false },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
        <Link to="/" className="flex items-center" aria-label="SyncTech — strona główna">
          <Logo className="h-9 w-auto text-foreground" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navItems.slice(0, 2).map((item) => (
            <NavItem key={item.key} item={item} active={activeSection === item.key} />
          ))}

          <div
            className="relative"
            onMouseEnter={() => setPlatOpen(true)}
            onMouseLeave={() => setPlatOpen(false)}
          >
            <button className="nav-link text-sm text-muted-foreground transition hover:text-foreground">
              Platformy
            </button>
            {platOpen && (
              <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="glass min-w-[220px] rounded-xl p-2 shadow-elegant">
                  {platforms.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="block rounded-lg px-3 py-2 text-sm transition hover:bg-accent hover:translate-x-1"
                    >
                      {p.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navItems.slice(2).map((item) => (
            <NavItem key={item.key} item={item} active={activeSection === item.key} />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            asChild
            size="sm"
            variant="premium"
            className="hidden md:inline-flex"
          >
            <a href="/#kontakt" onClick={() => trackEvent("cta_book_call", { location: "header", method: "form" })}>Umów konsultację</a>
          </Button>
          <button
            className="md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass mt-3 border-t border-border md:hidden">
          <div className="flex flex-col gap-1 p-4">
            <Link to="/" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Strona główna
            </Link>
            <a href="/#uslugi" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Usługi
            </a>
            <Link to="/cennik" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Cennik
            </Link>
            <Link to="/kalkulator" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Kalkulator
            </Link>
            <Link to="/blog" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Blog
            </Link>
            <Link to="/porownanie" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              Porównania
            </Link>
            <a href="/#faq" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-accent">
              FAQ
            </a>
            <p className="px-3 pt-3 text-xs uppercase tracking-wider text-muted-foreground">Platformy</p>
            {platforms.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                onClick={() => setOpen(false)}
                className="rounded px-3 py-2 text-sm hover:bg-accent"
              >
                {p.name}
              </Link>
            ))}
            <Button
              asChild
              size="sm"
              variant="premium"
              className="mt-3"
            >
              <a
                href="/#kontakt"
                onClick={() => {
                  trackEvent("cta_book_call", { location: "header_mobile", method: "form" });
                  setOpen(false);
                }}
              >
                Umów konsultację
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

type NavItemProps = {
  item: { key: string; label: string; href: string; isLink: boolean };
  active: boolean;
};

function NavItem({ item, active }: NavItemProps) {
  const baseClass =
    "relative text-sm transition-colors duration-300 " +
    (active ? "text-foreground" : "text-muted-foreground hover:text-foreground");

  const inner = (
    <>
      <span className="relative z-10">{item.label}</span>
      {active && (
        <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full bg-gradient-to-r from-primary via-primary-glow to-primary" />
      )}
      {active && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-x-2 -inset-y-1 -z-10 rounded-md bg-primary/5 blur-sm"
        />
      )}
    </>
  );

  if (item.isLink) {
    return (
      <Link to={item.href} className={baseClass}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={item.href} className={baseClass}>
      {inner}
    </a>
  );
}
